import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import { map, isNil, times } from 'lodash'
import cx from 'classnames'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import GhostContentAPI from '@tryghost/content-api'

import LinkButton from 'components/LinkButton'
import {
  CONTACT_ROUTE,
  KNOWLEDGE_ROUTE,
  SITEMAP_ROUTE,
  BLOG_ROUTE,
} from 'routes/constants'
import { tags } from 'data/tags'
import { Skeleton } from '@mui/material'

const api = new GhostContentAPI({
  host: 'https://buildwithstudio.com/knowledge',
  key: '3f75d060aee989ce5834d4ba3d',
  version: 'v5.0',
})

const Footer = ({ theme, isContactPage, isServicesPage, isProjectPage }) => {
  const breakpoints = useBreakpoint()
  const { sm: isTablet } = breakpoints || {}
  const [articles, setArticles] = useState([])
  const [isArticlesLoading, setIsArticlesLoading] = useState(true)

  useEffect(() => {
    api.posts
      .browse({
        limit: 5,
        include: 'tags',
      })
      .then((posts) => {
        const newArticles = map(posts, (post) => ({
          title: post.title,
          route: `/knowledge/${post.slug}/`,
        }))
        setArticles(newArticles)
        setIsArticlesLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setIsArticlesLoading(false)
      })
  }, [])

  function renderArticles() {
    if (isArticlesLoading) {
      return times(5, (index) => (
        <li key={`footer-article_${index}`}>
          <div className="footer-article__item --loading">
            <Skeleton
              variant="text"
              width="100%"
              height={32}
              sx={{ bgcolor: 'white' }}
            />
          </div>
          <div className="line-break" />
        </li>
      ))
    }

    return map(articles, ({ title, route }, index) => (
      <li key={`footer-article_${index}`}>
        <Link to={route} className="footer-article__item">
          {title}
        </Link>
        <div className="line-break" />
      </li>
    ))
  }

  return (
    <footer
      className={cx(`site__footer --${theme}`, {
        '--is-contact-page': isContactPage,
        '--is-services-page': isServicesPage,
        '--is-project-page': isProjectPage,
      })}
    >
      {!isContactPage && (
        <div className="footer__heading">
          <h2>Let&apos;s talk</h2>
          <p className="footer__paragraph --large">
            Whether your startup is looking to launch its first product, or an
            enterprise looking for help transforming an existing offering,
            Studio can {isTablet && 'build it with you.'}
          </p>
          {!isTablet && (
            <div className="footer__paragraph --inline">
              <p>build it with you .</p>
              <span className="line" />
              <LinkButton isTertiary route={CONTACT_ROUTE} text="Contact us" />
            </div>
          )}
          {isTablet && (
            <LinkButton isTertiary route={CONTACT_ROUTE} text="Contact us" />
          )}
        </div>
      )}
      <div className="footer__content">
        <div className="footer__content-column">
          <h3>Expertise in</h3>
          <ul className="footer__seo-links">
            {map(tags, ({ route, value }, index) => (
              <li key={`seo-tag_${index}`}>
                <div className="footer__list-style-type">
                  <span className="ball" />
                  <span className="line" />
                </div>
                {!isNil(route) ? (
                  <Link to={route} className="footer-tags__item">
                    {value}
                  </Link>
                ) : (
                  <span className="footer-tags__item">{value}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="footer__content-column">
          <div className="footer__column-heading">
            <h3>Knowledge</h3>
            <div>
              <LinkButton route={KNOWLEDGE_ROUTE} isSecondary text="Read all" />
            </div>
          </div>
          <ul className="footer__articles">{renderArticles()}</ul>
        </div>
      </div>
      <div className="footer__links">
        <div className="footer__links-container">
          <Link to={SITEMAP_ROUTE} className="footer__links-item">
            Sitemap
          </Link>
          <Link to={BLOG_ROUTE} className="footer__links-item">
            Blog
          </Link>
        </div>
        <p className="site__footer-copyright">
          © Build With Studio {new Date().getFullYear()}.
        </p>
      </div>
    </footer>
  )
}

Footer.propTypes = {
  theme: PropTypes.string,
  isContactPage: PropTypes.bool,
  isServicesPage: PropTypes.bool,
  isProjectPage: PropTypes.bool,
}

export default Footer
